import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Settings, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { useCycleConfig, useSetCycleConfig } from '@/hooks/usePerformance';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTranslation } from 'react-i18next';

export function CycleConfigPanel() {
  const { t } = useTranslation();
  const { data: config, isLoading } = useCycleConfig();
  const setConfig = useSetCycleConfig();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    eligibilityCutoff: '',
    periodStart:       '2026-01-01',
    periodEnd:         '2026-08-30',
    cycleLabel:        'Ciclo 2026',
    topPerformerCapPct: 5,
  });

  useEffect(() => {
    if (config) setForm(config);
  }, [config]);

  function handleChange(field: keyof typeof form, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    await setConfig.mutateAsync({
      ...form,
      topPerformerCapPct: Number(form.topPerformerCapPct) || 5,
    });
    setOpen(false);
  }

  return (
    <Card>
      <CardHeader
        className="pb-2 cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}
      >
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-muted-foreground" />
            {t('performance.cycle_config.title')}
          </span>
          {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </CardTitle>
      </CardHeader>

      {open && (
        <CardContent className="pt-0 space-y-3">
          {isLoading ? (
            <WinclapLoader size={20} />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">{t('performance.cycle_config.cycle_label_field')}</label>
                  <Input
                    value={form.cycleLabel}
                    onChange={e => handleChange('cycleLabel', e.target.value)}
                    placeholder={t('performance.cycle_config.cycle_label_placeholder')}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">{t('performance.cycle_config.eligibility_cutoff')}</label>
                  <Input
                    type="date"
                    value={form.eligibilityCutoff}
                    onChange={e => handleChange('eligibilityCutoff', e.target.value)}
                    className="h-8 text-sm"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t('performance.cycle_config.cutoff_hint', 'Vacío = 3 meses rodantes automáticos')}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">{t('performance.cycle_config.period_start')}</label>
                  <Input
                    type="date"
                    value={form.periodStart}
                    onChange={e => handleChange('periodStart', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">
                    {t('performance.cycle_config.top_cap', 'Tope de Top Performers (%)')}
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={form.topPerformerCapPct}
                    onChange={e => setForm(prev => ({ ...prev, topPerformerCapPct: Number(e.target.value) }))}
                    className="h-8 text-sm"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t('performance.cycle_config.top_cap_hint', 'El agente desafía al líder que supere este % de Tops en su equipo')}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">{t('performance.cycle_config.period_end')}</label>
                  <Input
                    type="date"
                    value={form.periodEnd}
                    onChange={e => handleChange('periodEnd', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('performance.cycle_config.eligibility_note')}
              </p>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={setConfig.isPending}
                className="gap-2"
              >
                {setConfig.isPending ? <WinclapLoader size={14} /> : <Save className="h-4 w-4" />}
                {t('performance.cycle_config.save_config')}
              </Button>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
